import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, FolderKanban, Cpu, FileWarning, Send, X } from 'lucide-react'
import { searchApi, type SearchResult } from '../../services/searchApi'

const TYPE_ICONS: Record<string, typeof FolderKanban> = {
  project: FolderKanban,
  equipment: Cpu,
  issue: FileWarning,
  submittal: Send,
}

const TYPE_LABELS: Record<string, string> = {
  project: 'Project',
  equipment: 'Equipment',
  issue: 'Issue',
  submittal: 'Submittal',
}

export function GlobalSearch() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keyboard shortcut: Ctrl+K or Cmd+K to focus search
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      }
      if (e.key === 'Escape') {
        setOpen(false)
        inputRef.current?.blur()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  function handleChange(value: string) {
    setQuery(value)
    setSelectedIndex(-1)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (value.trim().length < 2) {
      setResults([])
      setOpen(value.length > 0)
      return
    }

    setOpen(true)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const data = await searchApi.search(value)
        setResults(data.results)
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 250)
  }

  function handleSelect(result: SearchResult) {
    setOpen(false)
    setQuery('')
    setResults([])
    navigate(result.url)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      e.preventDefault()
      handleSelect(results[selectedIndex])
    }
  }

  return (
    <div ref={containerRef} className="global-search" style={{ padding: '0 var(--spacing-4)', marginBottom: 'var(--spacing-2)' }}>
      <div className="global-search-input-wrapper" style={{ position: 'relative' }}>
        <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
        <input
          ref={inputRef}
          type="text"
          className="form-input"
          placeholder="Search... (Ctrl+K)"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (query.length >= 2) setOpen(true) }}
          onKeyDown={handleKeyDown}
          style={{
            paddingLeft: '32px',
            paddingRight: query ? '28px' : '8px',
            fontSize: 'var(--font-size-sm)',
            height: '34px',
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border)',
          }}
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]); setOpen(false) }}
            style={{
              position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
              padding: '2px', display: 'flex',
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="global-search-results" style={{
          position: 'absolute',
          left: 'var(--spacing-4)',
          right: 'var(--spacing-4)',
          top: '100%',
          marginTop: '4px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 100,
          maxHeight: '360px',
          overflowY: 'auto',
        }}>
          {loading && (
            <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
              Searching...
            </div>
          )}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
              No results found
            </div>
          )}
          {!loading && query.length < 2 && query.length > 0 && (
            <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
              Type at least 2 characters...
            </div>
          )}
          {results.map((result, index) => {
            const Icon = TYPE_ICONS[result.type] || Search
            return (
              <button
                key={`${result.type}-${result.id}`}
                onClick={() => handleSelect(result)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '10px 16px',
                  background: index === selectedIndex ? 'var(--bg-hover)' : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--border)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'var(--text-primary)',
                  fontSize: 'var(--font-size-sm)',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <Icon size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {result.title}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {TYPE_LABELS[result.type]}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
